import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  PuzzlePieceIcon,
  ClockIcon,
  TrophyIcon,
  StarIcon,
  LightBulbIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, BadgeIcon, InlineSpinner, EmptyState, Button } from '@/components/ui'
import { gamificationApi } from '@/api'
import type { EscapeRoom, EscapeRoomSession, EscapeRoomStage, EscapeRoomChallenge } from '@/api/gamification'

type EscapeRoomWithDetails = EscapeRoom & {
  stages: EscapeRoomStage[]
  leaderboard: {
    user: { id: number; full_name: string }
    time_seconds: number
    completed_at: string
  }[]
}

export default function EscapeRoomPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  // List view state
  const [escapeRooms, setEscapeRooms] = useState<EscapeRoom[]>([])
  const [loadingList, setLoadingList] = useState(true)

  // Detail view state
  const [escapeRoom, setEscapeRoom] = useState<EscapeRoomWithDetails | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  // Game session state
  const [session, setSession] = useState<EscapeRoomSession | null>(null)
  const [currentChallenge, setCurrentChallenge] = useState<EscapeRoomChallenge | null>(null)
  const [answer, setAnswer] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [showHint, setShowHint] = useState(false)
  const [hintText, setHintText] = useState('')
  const [hintsRemaining, setHintsRemaining] = useState(3)
  const [elapsedTime, setElapsedTime] = useState(0)

  // Timer
  useEffect(() => {
    let interval: NodeJS.Timeout
    if (session && !session.is_completed) {
      interval = setInterval(() => {
        const startTime = new Date(session.started_at).getTime()
        const now = Date.now()
        setElapsedTime(Math.floor((now - startTime) / 1000))
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [session])

  useEffect(() => {
    if (slug) {
      fetchEscapeRoomDetail()
    } else {
      fetchEscapeRooms()
    }
  }, [slug])

  const fetchEscapeRooms = async () => {
    setLoadingList(true)
    try {
      const data = await gamificationApi.getEscapeRooms()
      setEscapeRooms(data)
    } catch (error) {
      console.error('Error fetching escape rooms:', error)
    } finally {
      setLoadingList(false)
    }
  }

  const fetchEscapeRoomDetail = async () => {
    if (!slug) return
    setLoadingDetail(true)
    try {
      const data = await gamificationApi.getEscapeRoom(slug)
      setEscapeRoom(data)
    } catch (error) {
      console.error('Error fetching escape room:', error)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleStartGame = async () => {
    if (!escapeRoom) return
    try {
      const sessionData = await gamificationApi.startEscapeRoom(escapeRoom.id)
      setSession(sessionData)
      // Set first challenge from first stage
      if (escapeRoom.stages.length > 0 && escapeRoom.stages[0].challenges.length > 0) {
        setCurrentChallenge(escapeRoom.stages[0].challenges[0])
      }
    } catch (error) {
      console.error('Error starting game:', error)
    }
  }

  const handleSubmitAnswer = async () => {
    if (!session || !currentChallenge || !answer.trim()) return
    setSubmitting(true)
    setFeedback(null)

    try {
      const result = await gamificationApi.submitChallengeAnswer(session.id, currentChallenge.id, answer)

      if (result.is_correct) {
        if (result.escape_completed) {
          setFeedback({ type: 'success', message: `Complimenti! Hai completato l'escape room e guadagnato ${result.points_earned} punti!` })
          setSession(prev => prev ? { ...prev, is_completed: true, points_earned: result.points_earned || 0 } : null)
          setCurrentChallenge(null)
        } else if (result.next_challenge) {
          setFeedback({ type: 'success', message: 'Corretto! Passiamo alla prossima sfida...' })
          setTimeout(() => {
            setCurrentChallenge(result.next_challenge!)
            setAnswer('')
            setFeedback(null)
            setShowHint(false)
            setHintText('')
          }, 1500)
        }
      } else {
        setFeedback({ type: 'error', message: 'Risposta non corretta. Riprova!' })
      }
    } catch (error) {
      console.error('Error submitting answer:', error)
      setFeedback({ type: 'error', message: 'Errore nell\'invio della risposta' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleUseHint = async () => {
    if (!session || !currentChallenge) return
    try {
      const result = await gamificationApi.useHint(session.id, currentChallenge.id)
      setHintText(result.hint)
      setHintsRemaining(result.hints_remaining)
      setShowHint(true)
    } catch (error) {
      console.error('Error getting hint:', error)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const getDifficultyBadge = (difficulty: EscapeRoom['difficulty']) => {
    const config: Record<EscapeRoom['difficulty'], { variant: 'success' | 'warning' | 'danger'; label: string }> = {
      easy: { variant: 'success', label: 'Facile' },
      medium: { variant: 'warning', label: 'Medio' },
      hard: { variant: 'danger', label: 'Difficile' },
    }
    return config[difficulty]
  }

  // List View
  if (!slug) {
    if (loadingList) {
      return <InlineSpinner message="Caricamento escape room..." />
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Escape Room</h1>
            <p className="text-gray-600">Metti alla prova le tue conoscenze di compliance</p>
          </div>
          <Link
            to="/gamification/leaderboard"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <TrophyIcon className="h-5 w-5 mr-2" />
            Classifica
          </Link>
        </div>

        {/* Escape Rooms Grid */}
        {escapeRooms.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {escapeRooms.map((room) => {
              const difficultyConfig = getDifficultyBadge(room.difficulty)

              return (
                <Link key={room.id} to={`/gamification/escape-rooms/${room.slug}`} className="group">
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    {/* Thumbnail */}
                    <div className="relative h-40 -mx-4 -mt-4 mb-4 overflow-hidden rounded-t-lg bg-gradient-to-br from-purple-500 to-indigo-700">
                      {room.thumbnail_url ? (
                        <img src={room.thumbnail_url} alt={room.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <PuzzlePieceIcon className="h-16 w-16 text-white/30" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <Badge variant={difficultyConfig.variant}>{difficultyConfig.label}</Badge>
                      </div>
                      {room.is_completed && (
                        <div className="absolute bottom-2 left-2">
                          <Badge variant="success">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Completata
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                        {room.title}
                      </h3>

                      <p className="text-sm text-gray-500 line-clamp-2">{room.description}</p>

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          {room.time_limit_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          <StarIcon className="h-4 w-4" />
                          {room.points_reward} punti
                        </span>
                      </div>

                      {room.best_time_seconds && (
                        <div className="pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-400">
                            Miglior tempo: <span className="font-medium text-gray-600">{formatTime(room.best_time_seconds)}</span>
                          </p>
                        </div>
                      )}

                      {room.badge && (
                        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                          <BadgeIcon icon={room.badge.icon} className="w-6 h-6" />
                          <span className="text-sm text-gray-600">Badge: {room.badge.name}</span>
                        </div>
                      )}
                    </div>
                  </Card>
                </Link>
              )
            })}
          </div>
        ) : (
          <Card>
            <EmptyState
              icon={<PuzzlePieceIcon className="h-12 w-12" />}
              title="Nessuna escape room"
              description="Le escape room saranno disponibili a breve"
            />
          </Card>
        )}
      </div>
    )
  }

  // Detail/Game View
  if (loadingDetail) {
    return <InlineSpinner message="Caricamento escape room..." />
  }

  if (!escapeRoom) {
    return (
      <EmptyState
        icon={<PuzzlePieceIcon className="h-12 w-12" />}
        title="Escape room non trovata"
        description="L'escape room richiesta non esiste"
        action={{
          label: 'Torna alle Escape Room',
          onClick: () => navigate('/gamification/escape-rooms'),
        }}
      />
    )
  }

  const difficultyConfig = getDifficultyBadge(escapeRoom.difficulty)

  // Game in progress
  if (session && !session.is_completed && currentChallenge) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Game Header */}
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{escapeRoom.title}</h1>
              <Badge variant={difficultyConfig.variant}>{difficultyConfig.label}</Badge>
            </div>
            <div className="text-right">
              <p className="text-3xl font-mono font-bold text-primary-600">{formatTime(elapsedTime)}</p>
              <p className="text-xs text-gray-500">Tempo trascorso</p>
            </div>
          </div>
        </Card>

        {/* Challenge */}
        <Card>
          <div className="space-y-6">
            <div className="text-center">
              <PuzzlePieceIcon className="h-12 w-12 text-primary-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Sfida</h2>
              <p className="text-gray-600">{currentChallenge.question}</p>
            </div>

            {/* Hint */}
            {showHint && hintText && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <LightBulbIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Suggerimento</p>
                    <p className="text-sm text-yellow-700 mt-1">{hintText}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Answer Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">La tua risposta</label>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmitAnswer()}
                placeholder="Inserisci la tua risposta..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-lg"
                disabled={submitting}
              />
            </div>

            {/* Feedback */}
            {feedback && (
              <div className={`p-4 rounded-lg ${feedback.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                <div className="flex items-center gap-2">
                  {feedback.type === 'success' ? (
                    <CheckCircleIcon className="h-5 w-5" />
                  ) : (
                    <XCircleIcon className="h-5 w-5" />
                  )}
                  {feedback.message}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <button
                onClick={handleUseHint}
                disabled={hintsRemaining === 0 || showHint}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LightBulbIcon className="h-5 w-5" />
                Suggerimento ({hintsRemaining} rimasti)
              </button>
              <Button onClick={handleSubmitAnswer} disabled={!answer.trim() || submitting}>
                {submitting ? 'Invio...' : 'Verifica risposta'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  // Game completed
  if (session?.is_completed) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <div className="text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <TrophyIcon className="h-10 w-10 text-green-600" />
            </div>

            <div>
              <h1 className="text-2xl font-bold text-gray-900">Complimenti!</h1>
              <p className="text-gray-600 mt-2">Hai completato "{escapeRoom.title}"</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-primary-600">{formatTime(elapsedTime)}</p>
                <p className="text-sm text-gray-500">Tempo impiegato</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">+{session.points_earned}</p>
                <p className="text-sm text-gray-500">Punti guadagnati</p>
              </div>
            </div>

            {escapeRoom.badge && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 mb-2">Hai sbloccato un nuovo badge!</p>
                <div className="flex items-center justify-center gap-2">
                  <BadgeIcon icon={escapeRoom.badge.icon} className="w-10 h-10" />
                  <span className="font-semibold text-gray-900">{escapeRoom.badge.name}</span>
                </div>
              </div>
            )}

            <div className="flex gap-4 justify-center pt-4">
              <Link
                to="/gamification/escape-rooms"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Torna alle Escape Room
              </Link>
              <Link
                to="/gamification/leaderboard"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Vedi classifica
              </Link>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  // Detail View (not started)
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/gamification/escape-rooms" className="hover:text-primary-600">Escape Room</Link>
        <ChevronRightIcon className="h-4 w-4" />
        <span className="text-gray-900">{escapeRoom.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            {/* Header Image */}
            <div className="relative h-64 -mx-4 -mt-4 mb-6 overflow-hidden rounded-t-lg bg-gradient-to-br from-purple-500 to-indigo-700">
              {escapeRoom.thumbnail_url ? (
                <img src={escapeRoom.thumbnail_url} alt={escapeRoom.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <PuzzlePieceIcon className="h-24 w-24 text-white/30" />
                </div>
              )}
              <div className="absolute top-4 right-4">
                <Badge variant={difficultyConfig.variant} size="lg">{difficultyConfig.label}</Badge>
              </div>
              {escapeRoom.is_completed && (
                <div className="absolute bottom-4 left-4">
                  <Badge variant="success" size="lg">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Completata
                  </Badge>
                </div>
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">{escapeRoom.title}</h1>
            <p className="text-gray-600">{escapeRoom.description}</p>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600">{escapeRoom.time_limit_minutes} minuti</span>
              </div>
              <div className="flex items-center gap-2">
                <StarIcon className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600">{escapeRoom.points_reward} punti</span>
              </div>
              <div className="flex items-center gap-2">
                <PuzzlePieceIcon className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600">{escapeRoom.stages.length} fasi</span>
              </div>
            </div>
          </Card>

          {/* Leaderboard */}
          {escapeRoom.leaderboard.length > 0 && (
            <Card>
              <CardHeader title="Classifica migliori tempi" />
              <div className="space-y-2">
                {escapeRoom.leaderboard.slice(0, 10).map((entry, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-4 p-3 rounded-lg ${
                      index < 3 ? 'bg-primary-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className={`w-8 text-center font-bold ${
                      index === 0 ? 'text-yellow-500' :
                      index === 1 ? 'text-gray-400' :
                      index === 2 ? 'text-orange-500' : 'text-gray-400'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="flex-1 font-medium text-gray-900">{entry.user.full_name}</span>
                    <span className="font-mono text-primary-600">{formatTime(entry.time_seconds)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Start Game Card */}
          <Card>
            <div className="text-center space-y-4">
              {escapeRoom.is_completed ? (
                <>
                  <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <p className="text-green-600 font-medium">Hai già completato questa escape room</p>
                  {escapeRoom.best_time_seconds && (
                    <p className="text-sm text-gray-500">
                      Miglior tempo: <span className="font-mono font-medium">{formatTime(escapeRoom.best_time_seconds)}</span>
                    </p>
                  )}
                  <Button className="w-full" onClick={handleStartGame}>
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Rigioca
                  </Button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto bg-primary-100 rounded-full flex items-center justify-center">
                    <PlayIcon className="h-8 w-8 text-primary-600" />
                  </div>
                  <p className="text-gray-600">Pronto a mettere alla prova le tue conoscenze?</p>
                  <Button className="w-full" onClick={handleStartGame}>
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Inizia la sfida
                  </Button>
                </>
              )}
            </div>
          </Card>

          {/* Badge Reward */}
          {escapeRoom.badge && (
            <Card>
              <CardHeader title="Premio" />
              <div className="text-center">
                <BadgeIcon icon={escapeRoom.badge.icon} className="w-16 h-16 mx-auto" />
                <p className="font-medium text-gray-900 mt-2">{escapeRoom.badge.name}</p>
                <p className="text-sm text-gray-500 mt-1">{escapeRoom.badge.description}</p>
              </div>
            </Card>
          )}

          {/* Info */}
          <Card>
            <CardHeader title="Informazioni" />
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Difficoltà</dt>
                <dd><Badge variant={difficultyConfig.variant}>{difficultyConfig.label}</Badge></dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Tempo limite</dt>
                <dd className="font-medium text-gray-900">{escapeRoom.time_limit_minutes} min</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Punti</dt>
                <dd className="font-medium text-gray-900">{escapeRoom.points_reward}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Tentativi</dt>
                <dd className="font-medium text-gray-900">{escapeRoom.attempts_count}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  )
}
